import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { useTransportRefData } from "./shared/useTransportRefData";
import { PaginationBar } from "@/components/shared/PaginationBar";

export default function HistoryTab() {
  const { rigs } = useTransportRefData();
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_trips: 0, total_distance_km: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ tripType: "all", status: "all", rigAssetId: "all", dateFrom: "", dateTo: "", search: "" });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { page: pageNum, page_size: 50 };
    if (filters.tripType !== "all") params.trip_type = filters.tripType;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.rigAssetId !== "all") params.rig_asset_id = filters.rigAssetId;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.search.trim()) params.search = filters.search.trim();
    client
      .get("/transport-log/history", { params })
      .then((res) => {
        setEntries(res.data.entries);
        setTotals({ total_trips: res.data.total_trips, total_distance_km: res.data.total_distance_km });
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(1), [filters.tripType, filters.status, filters.rigAssetId, filters.dateFrom, filters.dateTo, filters.search]);

  return (
    <div className="space-y-4" data-testid="transport-history-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            data-testid="history-search-input"
            placeholder="Search Trip ID or Rig..."
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
            className="pl-8 w-56"
          />
        </div>
        <Select value={filters.tripType} onValueChange={(v) => set("tripType", v)}>
          <SelectTrigger data-testid="history-type-filter" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Rig">Rig</SelectItem>
            <SelectItem value="Material">Material</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger data-testid="history-status-filter" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="In Transit">In Transit</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <SearchableSelect testId="history-rig-filter" className="w-44" value={filters.rigAssetId} onValueChange={(v) => set("rigAssetId", v)} placeholder="All Rigs"
  options={[{ value: "all", label: "All Rigs", pinned: true }, ...rigs.map((r) => ({ value: r.asset_id, label: r.label, testId: `history-rig-option-${r.asset_id}` }))]} />
        <Input type="date" data-testid="history-date-from-input" value={filters.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} className="w-40" />
        <Input type="date" data-testid="history-date-to-input" value={filters.dateTo} onChange={(e) => set("dateTo", e.target.value)} className="w-40" />
      </div>

      <div className="flex gap-4">
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs uppercase text-slate-400 font-mono">Total Trips</p>
          <p className="text-xl font-bold text-slate-900" data-testid="history-total-trips">
            {totals.total_trips}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs uppercase text-slate-400 font-mono">Total Distance (km)</p>
          <p className="text-xl font-bold text-slate-900" data-testid="history-total-distance">
            {totals.total_distance_km}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="history-table">
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Trip ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Rig</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Distance (km)</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  No location updates found.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`history-row-${e.id}`}>
                  <TableCell>{new Date(e.timestamp).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="font-mono text-xs">{e.trip_id}</TableCell>
                  <TableCell>{e.trip_type}</TableCell>
                  <TableCell className="font-mono text-xs">{e.rig_label || "—"}</TableCell>
                  <TableCell>
                    <Badge className={e.status === "Completed" ? "bg-emerald-600" : "bg-amber-500"}>{e.status}</Badge>
                  </TableCell>
                  <TableCell>{e.location_reference}</TableCell>
                  <TableCell>{e.distance_km != null ? e.distance_km : "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{e.remarks || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="history-pagination" />
    </div>
  );
}
